import React, {useState, useRef, useContext, useMemo, useCallback, useEffect} from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';

import {CSSTransition} from 'react-transition-group';

import BookmarksButton from '~/src/components/bookmarksButton/BookmarksButton';
import RatingButton from '~/src/components/rating/button/RatingButton';
import ShareButton from '~/src/components/share/button/ShareButton';
import Bubbles from '~/src/components/svg/action/Bubbles';
import CommentButton from '~/src/components/comment/button/CommentButton';

import {classNames, getFormatClassNames} from '~/src/utilities/helpers/css';
import {ActionBubbleFormat, ButtonFormat, ColorScheme, TeaserFormat} from '~/src/utilities/constants/formats';
import {isEnabled} from '~/src/utilities/helpers/feature';
import {Feature} from '~/src/utilities/constants/feature';
import {TypeContext} from '~/src/components/context/TypeContext';
import {SettingsContext} from '~/src/components/context/SettingsContext';
import {SectionContext} from '~/src/components/context/SectionContext';
import {TeaserTypes} from '~/src/utilities/constants/types';
import {InteractionSources, GtmEvents} from '~/src/utilities/constants/tracking';
import {trackGtm} from '~/src/utilities/tracking/gtm';
import {SectionLayout} from '~/src/utilities/constants/layout';
import {ContentStyle} from '~/src/utilities/constants/style';

import css from './ActionBubble.module.scss';

const ActionBubble = (props) => {
    const settings = useContext(SettingsContext);
    const type = useContext(TypeContext);
    const section = useContext(SectionContext);

    const [isActive, setIsActive] = useState(false);

    const containerRef = useRef();

    const interactionSource = useMemo(() => {
        const isTeaser = Object.values(TeaserTypes).includes(type);
        return isTeaser ? InteractionSources.BUBBLE_TEASER : InteractionSources.BUBBLE_ARTICLE;
    }, [type]);

    const onClick = useCallback((event) => {
        if (!containerRef.current.contains(event.target)) {
            event.preventDefault();
            setIsActive(false);
        }
    }, []);

    const onButtonClick = useCallback(
        (event) => {
            event.preventDefault();

            setIsActive(!isActive);

            trackGtm(GtmEvents.INTERACTION, {
                interactionType: isActive ? 'close' : 'open',
                interactionSource: interactionSource,
                contentId: props.metadataId
            });
        },
        [isActive]
    );

    const closeActionBubble = useCallback(() => setIsActive(false), []);

    process.browser &&
        useEffect(() => {
            if (isActive) {
                document.body.addEventListener('click', onClick);
            }

            return () => document.body.removeEventListener('click', onClick);
        }, [isActive, onClick]);

    const format = props.format || ActionBubbleFormat.ACTION_BUBBLE_TEASER;
    const colorScheme = get(props, 'teaserFormat.colorScheme');
    const isLifestyleSection = section.layout === SectionLayout.LIFESTYLE || get(props, 'styles.name') === ContentStyle.LIFESTYLE;
    const containerClasses = classNames(css, 'actionBubble', format, getFormatClassNames(props.teaserFormat), type, settings.siteArea, {
        isActive,
        isLifestyleSection
    });

    const transitionClasses = {
        enter: css.bubbleEnter,
        enterActive: css.bubbleEnterActive,
        enterDone: css.bubbleEnterDone,
        exit: css.bubbleExit,
        exitActive: css.bubbleExitActive,
        exitDone: css.bubbleExitDone
    };

    const duration = parseInt(css.animationDuration, 10);

    return (
        <div className={containerClasses} data-testid="ActionBubble" ref={containerRef}>
            <button className={css.toggle} onClick={onButtonClick}>
                <Bubbles primaryClassName={css.primaryColor} secondaryClassName={css.secondaryColor} tertiaryClassName={css.tertiaryColor} />
            </button>

            <div className={css.bubbles}>
                <CSSTransition classNames={transitionClasses} in={isActive} timeout={duration}>
                    <div className={css.bubble}>
                        <div className={css.bubbleButton}>
                            <RatingButton
                                metadataId={props.metadataId.toString()}
                                format={ButtonFormat.BUTTON_ACTION_BUBBLE}
                                colorScheme={colorScheme}
                                source={interactionSource}
                                isDisabled={!get(props, 'settings.likesEnabled', true)}
                                onClick={props.onRatingClick}
                            />
                        </div>
                    </div>
                </CSSTransition>

                <CSSTransition classNames={transitionClasses} in={isActive} timeout={duration}>
                    <div className={css.bubble}>
                        <div className={css.bubbleButton}>
                            <CommentButton
                                metadataId={props.metadataId.toString()}
                                format={ButtonFormat.BUTTON_ACTION_BUBBLE}
                                colorScheme={colorScheme}
                                isDisabled={!isEnabled(Feature.COMMENTS) || !get(props, 'settings.commentingEnabled')}
                                openCommentPage={props.openCommentPage}
                                source={interactionSource}
                            />
                        </div>
                    </div>
                </CSSTransition>

                <CSSTransition classNames={transitionClasses} in={isActive} timeout={duration}>
                    <div className={css.bubble}>
                        <div className={css.bubbleButton}>
                            <ShareButton
                                metadataId={props.metadataId.toString()}
                                format={ButtonFormat.BUTTON_ACTION_BUBBLE}
                                colorScheme={colorScheme}
                                closeActionBubble={closeActionBubble}
                                isDisabled={!isEnabled(Feature.SHARE) || !get(props, 'settings.sharingEnabled')}
                                source={interactionSource}
                            />
                        </div>
                    </div>
                </CSSTransition>

                <CSSTransition classNames={transitionClasses} in={isActive} timeout={duration}>
                    <div className={css.bubble}>
                        <div className={css.bubbleButton}>
                            <BookmarksButton
                                metadataId={props.metadataId.toString()}
                                format={ButtonFormat.BUTTON_ACTION_BUBBLE}
                                colorScheme={colorScheme}
                                isDisabled={!isEnabled(Feature.BOOKMARKS)}
                                source={interactionSource}
                            />
                        </div>
                    </div>
                </CSSTransition>
            </div>
        </div>
    );
};

if (process.env.NODE_ENV === 'development') {
    ActionBubble.propTypes = {
        metadataId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        format: PropTypes.oneOf(Object.values(ActionBubbleFormat)),
        teaserFormat: PropTypes.shape({
            primaryGrid: PropTypes.oneOf(Object.values(TeaserFormat)),
            secondaryGrid: PropTypes.oneOf(Object.values(TeaserFormat)),
            colorScheme: PropTypes.oneOf(Object.values(ColorScheme))
        }),
        settings: PropTypes.shape({
            commentingEnabled: PropTypes.bool,
            sharingEnabled: PropTypes.bool
        }),
        onRatingClick: PropTypes.func,
        styles: PropTypes.shape({
            name: PropTypes.string
        })
    };
}

export default ActionBubble;
